import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getPostBySlug, incrementPostViews } from '../utils/blogService';
import LandingNav from '../components/LandingNav';
import '../pages/Landing.css';
import './Blog.css';
import './BlogPost.css';

const COPY = {
  en: {
    loading: 'Loading…',
    notFound: 'Post not found.',
    back: '← All posts',
    allPosts: 'All posts',
    home: 'Home',
    contact: 'Contact us',
    rights: (year) => `© ${year} NOVAS. All rights reserved.`,
    dateLocale: 'en-US',
  },
  ru: {
    loading: 'Загрузка…',
    notFound: 'Пост не найден.',
    back: '← Ко всем постам',
    allPosts: 'Все посты',
    home: 'На главную',
    contact: 'Связаться с нами',
    rights: (year) => `© ${year} NOVAS. Все права защищены.`,
    dateLocale: 'ru-RU',
  },
};

function formatDate(iso, locale) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch (_) {
    return '';
  }
}

export default function BlogPost() {
  const { slug } = useParams();

  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('novas-landing-lang') || 'en'; }
    catch (_) { return 'en'; }
  });
  const t = COPY[lang];
  const toggleLang = () => setLang((prev) => (prev === 'en' ? 'ru' : 'en'));

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const viewedSlugRef = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-landing-theme', 'dark');
    return () => document.documentElement.removeAttribute('data-landing-theme');
  }, []);

  useEffect(() => {
    try { localStorage.setItem('novas-landing-lang', lang); } catch (_) {}
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const data = await getPostBySlug(slug);
        if (cancelled) return;
        if (!data) {
          setNotFound(true);
        } else {
          setPost(data);
          if (viewedSlugRef.current !== slug) {
            viewedSlugRef.current = slug;
            incrementPostViews(slug);
            setPost((prev) => (prev ? { ...prev, views: prev.views + 1 } : prev));
          }
        }
      } catch (e) {
        console.error('Error loading blog post:', e);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="landing blog-page" id="top">
      <LandingNav lang={lang} toggleLang={toggleLang} />

      <div className="blog-orb blog-orb--a" aria-hidden />
      <div className="blog-orb blog-orb--b" aria-hidden />

      <main className="blog-post-main">
        {loading ? (
          <p className="blog-list-empty">{t.loading}</p>
        ) : notFound || !post ? (
          <div className="blog-list-empty">
            <p>{t.notFound}</p>
            <Link to="/blog" className="landing-nav-cta-ghost blog-post-back-link">{t.back}</Link>
          </div>
        ) : (
          <article className="blog-post-article">
            {post.coverImageUrl && (
              <div className="blog-post-cover">
                <img src={post.coverImageUrl} alt={post.title} />
              </div>
            )}
            <div className="blog-post-meta">
              {formatDate(post.publishedAt, t.dateLocale)}
              {post.authorName && <> · {post.authorName}</>}
              <span className="blog-post-views">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
                  <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
                </svg>
                {post.views}
              </span>
            </div>
            <h1>{post.title}</h1>
            <div
              className="blog-post-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
            <Link to="/blog" className="landing-nav-cta-ghost blog-post-back-link">{t.back}</Link>
          </article>
        )}
      </main>

      <footer className="landing-footer">
        <div className="landing-footer-brand">{t.rights(new Date().getFullYear())}</div>
        <div className="landing-footer-links">
          <Link to="/">{t.home}</Link>
          <a href="mailto:novas.edtech@gmail.com">{t.contact}</a>
        </div>
      </footer>
    </div>
  );
}
